// N8's voice — a thin wrapper around the browser's Web Speech API that
// picks the softest, most natural voice available on the member's device
// and speaks with the pacing of a meditation guide (not a screen reader).
//
// Why the Web Speech API and not a cloud TTS?
//   • It is free, private (no audio ever leaves the device), needs no
//     API keys, and is available offline.
//   • Modern OSes ship high-quality neural voices — Samantha (Enhanced)
//     and Ava on macOS/iOS, Microsoft Aria/Guy on Windows, Google's
//     neural voices on Chrome/Android — and the difference from the old
//     robotic system voices is dramatic when we choose carefully.
//
// The functions in this module are safe to call on any browser: on
// unsupported browsers they simply no-op.

import { getVoicePref } from "@/lib/voicePref";

// ElevenLabs routing state — populated at boot via /api/tts/config. When
// enabled we play server-generated mp3 through an <Audio> element; when
// not, we fall back to the Web Speech synthesizer transparently.
const API_ROOT = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
let _cloudEnabled = false;
let _cloudAudio = null;      // currently-playing HTMLAudioElement
let _cloudSession = 0;       // guards async fetches through open/close races

if (typeof window !== "undefined") {
  fetch(`${API_ROOT}/api/tts/config`).then(r => r.ok ? r.json() : null)
    .then(cfg => { _cloudEnabled = !!(cfg && cfg.enabled); })
    .catch(() => { _cloudEnabled = false; });
}

function stopCloudAudio() {
  _cloudSession += 1;
  if (_cloudAudio) {
    try { _cloudAudio.pause(); _cloudAudio.src = ""; } catch { /* noop */ }
    _cloudAudio = null;
  }
}

/**
 * Play `text` via the server ElevenLabs proxy. `context` selects the
 * voice pair — "n8" (everywhere except meditations) or "meditation"
 * (meditations + visualizations). Falls back to Web Speech if the fetch
 * fails, so a network blip never leaves a member without narration.
 */

/**
 * Playback rate applied to the <audio> element for a given cloud
 * clip. Kristen (meditation · female) reads meaningfully slower than
 * Locke (meditation · male). Server-side speed is already at
 * ElevenLabs' max of 1.20; combining that with a 1.30× client-side
 * rate closes the remaining gap so both meditation voices land at a
 * similar felt tempo. `preservesPitch` is set on the audio element so
 * Kristen's timbre stays natural — no chipmunk artefact.
 */
function _playbackRateFor(context, gender) {
  if (context === "meditation" && gender === "female") return 1.30;
  return 1.0;
}

function speakCloud(text, { context = "n8", gender = "female", onEnd }) {
  stopCloudAudio();
  const mySession = ++_cloudSession;
  const url = `${API_ROOT}/api/tts?context=${encodeURIComponent(context)}` +
              `&gender=${encodeURIComponent(gender)}` +
              `&text=${encodeURIComponent(text)}`;
  const audio = new Audio(url);
  audio.preload = "auto";
  audio.volume = 0.9;
  const rate = _playbackRateFor(context, gender);
  if (rate !== 1.0) {
    audio.playbackRate = rate;
    // preservesPitch is the modern spec name; the WebKit/Chromium prefix
    // is set alongside for older browser versions. Keeping pitch preserves
    // Kristen's timbre while she reads at the faster male tempo.
    audio.preservesPitch = true;
    audio.mozPreservesPitch = true;
    audio.webkitPreservesPitch = true;
  }
  audio.onended = () => {
    if (mySession !== _cloudSession) return;
    _cloudAudio = null;
    onEnd?.();
  };
  audio.onerror = () => {
    if (mySession !== _cloudSession) return;
    _cloudAudio = null;
    onEnd?.();
  };
  _cloudAudio = audio;
  audio.play().catch(() => {
    if (mySession !== _cloudSession) return;
    _cloudAudio = null;
    onEnd?.();
  });
  return true;
}

let _currentUtter = null;
let _cachedVoices = null;
let _voicesReady = false;
// Every setTimeout used to schedule the next phrase (or the opening
// 480 ms settle-in delay) lives in this set so stopSpeaking() can cancel
// pending speech that hasn't started yet. Without this, closing a
// meditation before the first utterance fires would let narration begin
// after the overlay is already gone.
const _pendingTimers = new Set();
// Monotonic session id — each speak() call captures the current value,
// and any async continuation (in particular the ensureVoices().then()
// callback, which resolves *after* stopSpeaking() may have run on first
// load) checks that its captured id still matches. If stopSpeaking() or
// a newer speak() has bumped the id, the continuation bails out silently.
// This closes the "voices-not-ready" race that a plain timer set alone
// can't cover, because there is nothing to clear until the promise
// resolves.
let _sessionId = 0;

function scheduleSpeak(fn, ms) {
  const id = setTimeout(() => {
    _pendingTimers.delete(id);
    fn();
  }, ms);
  _pendingTimers.add(id);
  return id;
}

function clearAllPendingTimers() {
  for (const id of _pendingTimers) {
    clearTimeout(id);
  }
  _pendingTimers.clear();
}

function synth() {
  if (typeof window === "undefined") return null;
  return window.speechSynthesis || null;
}

/** Return true when the browser can speak — via ElevenLabs proxy or Web Speech. */
export function canSpeak() {
  if (_cloudEnabled) return true;
  return !!synth() && typeof window !== "undefined" && "SpeechSynthesisUtterance" in window;
}

/** True while a read-aloud is currently playing or queued. */
export function isSpeaking() {
  const s = synth();
  return !!(s && (s.speaking || s.pending));
}

/** Load and cache the voice list, resolving once the browser has them. */
function ensureVoices() {
  return new Promise((resolve) => {
    const s = synth();
    if (!s) { resolve([]); return; }
    const now = s.getVoices();
    if (now && now.length) {
      _cachedVoices = now;
      _voicesReady = true;
      resolve(now);
      return;
    }
    // Voices load asynchronously in some browsers (Chrome especially).
    const handler = () => {
      const v = s.getVoices();
      if (v && v.length) {
        _cachedVoices = v;
        _voicesReady = true;
        s.removeEventListener("voiceschanged", handler);
        resolve(v);
      }
    };
    s.addEventListener("voiceschanged", handler);
    // Fallback timer — some browsers never fire the event but the list
    // becomes available anyway after a short delay.
    setTimeout(() => {
      const v = s.getVoices();
      if (v && v.length) {
        _cachedVoices = v;
        _voicesReady = true;
        s.removeEventListener("voiceschanged", handler);
        resolve(v);
      } else {
        resolve([]);
      }
    }, 400);
  });
}

// Curated preference lists — the "Enhanced" and neural voices per platform
// that consistently read as warm, breathy and human rather than robotic.
// We match by substring on the voice name because full names differ
// slightly across OS versions ("Samantha (Enhanced)" vs "Samantha").
const FEMALE_PREF = [
  // macOS / iOS — Enhanced/Premium neural voices sound remarkably real
  "Samantha (Enhanced)", "Ava (Enhanced)", "Ava (Premium)",
  "Allison (Enhanced)", "Nicky (Enhanced)", "Serena (Enhanced)",
  "Susan (Enhanced)", "Karen (Enhanced)", "Zoe (Enhanced)",
  "Samantha", "Ava", "Allison", "Serena", "Susan", "Karen", "Zoe",
  "Moira", "Tessa",
  // Microsoft neural voices (Windows / Edge)
  "Microsoft Aria Online (Natural)", "Microsoft Jenny Online (Natural)",
  "Microsoft Ana Online (Natural)", "Microsoft Sonia Online (Natural)",
  "Aria", "Jenny", "Sonia", "Emma", "Libby",
  // Google — neural en-US/en-GB voices on Chrome/Android
  "Google UK English Female", "Google US English",
  // Generic fallbacks
  "female",
];

const MALE_PREF = [
  // macOS / iOS
  "Tom (Enhanced)", "Aaron (Enhanced)", "Daniel (Enhanced)",
  "Alex", "Daniel", "Fred", "Oliver", "Tom", "Aaron",
  // Microsoft neural
  "Microsoft Guy Online (Natural)", "Microsoft Davis Online (Natural)",
  "Microsoft Ryan Online (Natural)", "Microsoft Brandon Online (Natural)",
  "Guy", "Davis", "Ryan", "Brandon",
  // Google
  "Google UK English Male",
  // Generic fallbacks
  "male",
];

// Voices we explicitly avoid — these are known to sound robotic across
// devices and always give the "computer reading a document" feeling.
const AVOID = [
  "Fiona", "Veena", "Rishi", "Trinoids", "Whisper", "Bells", "Bahh",
  "Bad News", "Good News", "Cellos", "Deranged", "Hysterical",
  "Zarvox", "Albert", "Kathy", "Junior", "Ralph", "Bruce",
];

/**
 * Choose the softest, most natural voice matching the requested gender.
 * Preference order (english only):
 *   1. Curated "Enhanced/Neural" list for that gender.
 *   2. Any en-* voice whose name matches the gender's fallback tokens.
 *   3. First en-* voice on the device.
 *   4. First voice on the device.
 */
function pickVoice(voices, gender = "female") {
  if (!voices || !voices.length) return null;

  const isEn = (v) => v.lang && v.lang.toLowerCase().startsWith("en");
  const notAvoided = (v) => !AVOID.some((a) => v.name.toLowerCase().includes(a.toLowerCase()));

  const enVoices = voices.filter((v) => isEn(v) && notAvoided(v));
  const prefList = gender === "male" ? MALE_PREF : FEMALE_PREF;

  // 1. Walk the preference list in order — exact substring match wins.
  for (const wanted of prefList) {
    const w = wanted.toLowerCase();
    const hit = enVoices.find((v) => v.name.toLowerCase().includes(w));
    if (hit) return hit;
  }

  // 2. Best remaining en voice — prefer neural/enhanced/premium markers.
  const enhanced = enVoices.find((v) => /enhanced|premium|neural|natural/i.test(v.name));
  if (enhanced) return enhanced;

  // 3. Any en voice.
  if (enVoices.length) return enVoices[0];

  // 4. Give up gracefully — first available voice.
  return voices[0];
}

/**
 * Split text into speakable phrase chunks with breath-level pauses.
 *
 * A meditation voice doesn't just pause at periods — it pauses on
 * commas, dashes and semicolons too, letting each phrase land before
 * the next one begins. We split on all of these so the queue in speak()
 * can insert a small silence after every clause, mimicking a guide
 * taking a breath between thoughts.
 *
 * Chunks under three words are re-attached to the previous one so a
 * lone "and" or "or" doesn't get stranded on its own utterance.
 */
function splitPhrases(text) {
  const raw = String(text || "").replace(/\s+/g, " ").trim();
  if (!raw) return [];
  // Split at ., !, ?, …, ; and — (em dash) and , while keeping the
  // punctuation attached to the phrase before it. The regex captures
  // any run of non-breaking characters followed by one of the pause
  // punctuations, then the trailing punctuation itself.
  const parts = raw.match(/[^.!?…;—,]+[.!?…;—,]?/g) || [raw];
  const cleaned = parts.map((s) => s.trim()).filter(Boolean);
  // Fold tiny fragments back onto their predecessor.
  const merged = [];
  for (const p of cleaned) {
    const wordCount = p.split(/\s+/).length;
    if (merged.length && wordCount < 3) {
      merged[merged.length - 1] = `${merged[merged.length - 1]} ${p}`;
    } else {
      merged.push(p);
    }
  }
  return merged;
}

/**
 * How long to wait after a given chunk before speaking the next one.
 * Longer pauses after full sentences than after commas — the same
 * cadence a human guide uses.
 */
function pauseAfter(chunk) {
  const last = chunk.slice(-1);
  if (/[.!?…]/.test(last)) return 900;   // full stop → a real breath
  if (last === ";") return 700;
  if (last === "—") return 650;
  if (last === ",") return 500;
  return 380;                             // clause with no punctuation
}

/**
 * Speak text softly using the member's chosen N8 voice.
 *
 * Returns true when playback started (or was queued), false when the
 * browser refused (unsupported, empty text, no voice available).
 *
 * Options tuned for a meditation-guide read:
 *   • rate  — 0.78 by default. Slower than conversational. A trained
 *             meditation voice speaks at roughly this pace so words
 *             have time to land in the body.
 *   • pitch — female = 0.96, male = 0.88. A touch lower than the
 *             speaker's neutral pitch — this is the sonic signature
 *             of a calm, grounded voice.
 *   • volume — 0.85 for intimacy, leaving headroom for ambience.
 *   • gender — override the member's global preference for this one
 *              utterance (rarely needed).
 *   • onEnd  — called when the whole utterance finishes.
 */
export function speak(text, {
  rate,
  pitch,
  volume = 0.85,
  gender,
  context = "n8",
  onEnd,
} = {}) {
  if (!text) return false;
  const pref = getVoicePref();
  if (!pref.enabled) return false;
  const g = gender || pref.gender;

  // Route to ElevenLabs proxy when configured — falls back to Web Speech
  // on any network error via the audio.onerror handler.
  if (_cloudEnabled) {
    return speakCloud(text, { context, gender: g, onEnd });
  }

  const s = synth();
  if (!s) return false;

  // Meditation-guide defaults — deliberately slow and grounded.
  const finalRate = typeof rate === "number" ? rate : 0.78;
  const finalPitch = typeof pitch === "number"
    ? pitch
    : (g === "male" ? 0.88 : 0.96);

  const chunks = splitPhrases(text);
  if (chunks.length === 0) return false;

  try {
    s.cancel();
  } catch { /* noop */ }
  // Cancel any timers queued by a previous speak() call so their stale
  // closures don't fire against the new text.
  clearAllPendingTimers();

  // Claim a fresh session. If stopSpeaking() or a newer speak() runs
  // before ensureVoices() resolves, this snapshot will no longer match
  // the module-level _sessionId and startQueue will bail out.
  const mySession = ++_sessionId;

  const startQueue = (voices) => {
    if (mySession !== _sessionId) return; // stale — a stop/new speak happened
    const chosen = pickVoice(voices, g);
    let idx = 0;

    const speakNext = () => {
      if (idx >= chunks.length) {
        _currentUtter = null;
        onEnd?.();
        return;
      }
      const chunk = chunks[idx];
      const u = new SpeechSynthesisUtterance(chunk);
      u.rate = finalRate;
      u.pitch = finalPitch;
      u.volume = volume;
      if (chosen) u.voice = chosen;
      // A breath-length pause after each chunk — longer at full stops,
      // shorter at commas — so the whole read has the rise-and-fall of
      // a human meditation guide, never a run-on.
      u.onend = () => {
        idx += 1;
        scheduleSpeak(speakNext, pauseAfter(chunk));
      };
      u.onerror = () => {
        _currentUtter = null;
        onEnd?.();
      };
      _currentUtter = u;
      s.speak(u);
    };

    // A small settling pause before the first word — lets the member
    // arrive before the voice begins, the way a meditation teacher
    // waits a beat after saying "close your eyes".
    scheduleSpeak(speakNext, 480);
  };

  if (_voicesReady && _cachedVoices) {
    startQueue(_cachedVoices);
  } else {
    ensureVoices().then(startQueue);
  }
  return true;
}

/** Cancel any current playback and any queued next-phrase timers. */
export function stopSpeaking() {
  stopCloudAudio();
  const s = synth();
  // Invalidate any in-flight ensureVoices().then(startQueue) continuation.
  _sessionId += 1;
  clearAllPendingTimers();
  if (!s) return;
  try { s.cancel(); } catch { /* noop */ }
  _currentUtter = null;
}

/**
 * List the friendly names of the voices we'd actually use for each
 * gender on this device — powers the "Currently: Samantha (Enhanced)"
 * label on the Profile settings card so members know what they'll hear.
 */
export async function getSelectedVoiceNames() {
  const voices = await ensureVoices();
  return {
    female: pickVoice(voices, "female")?.name || null,
    male:   pickVoice(voices, "male")?.name   || null,
  };
}

/** Warm up the voice cache on app boot. */
export function primeVoices() {
  ensureVoices();
}

// Auto-prime as soon as the module loads.
if (typeof window !== "undefined") {
  primeVoices();
  // Some browsers only surface voices after a user gesture — nudge on
  // first pointer interaction as a belt-and-braces measure.
  const nudge = () => {
    ensureVoices();
    window.removeEventListener("pointerdown", nudge);
  };
  window.addEventListener("pointerdown", nudge, { once: true });
}
